import { useEffect } from 'react';
import { SwitchingIcon } from '@icons';

import {
  AddMemberModal,
  Button,
  ConfirmationModal,
  Spinner,
  Tabs
} from '@components';
import AddFromMembersListTab from '@features/group-management/components/v1/tabs/add-from-members-list-tab/AddFromMembersListTab';
import { AddMembersTabType } from '@features/group-management/constants';
import { getTermsListFromConfig } from '@features/group-management/helper';
import { getGroupAnalyticsSummary } from '@features/group-management/helpers/groupAnalytics';
import useAddMembersStep from '@features/group-management/hooks/useAddMembersStep';
import ConfirmationModalV2 from '@layouts/modal/ConfirmationModal.v2';

import theme from '../../../../../../themes/colors';
import AddManuallyTab from '../../v1/tabs/add-manually-tab/AddManuallyTab';
import ImportFileTab from '../../v1/tabs/import-file-tab/ImportFileTab';

interface MemberStepProps {
  groupId: string;
  onHeaderStateChange: (state: {
    onSave: () => void;
    isSaveDisabled: boolean;
    isLoading: boolean;
  }) => void;
  defaultTermLabel?: string;
}

const MemberStep = ({
  groupId,
  onHeaderStateChange,
  defaultTermLabel
}: MemberStepProps) => {
  const {
    selectedTab,
    onSelectTab,
    groupAddMemberTabs,
    memberList,
    selectedMember,
    isAddMemberModalOpen,
    isEditMemberModalOpen,
    selectedRows,
    setSelectedRows,
    openAddMemberModal,
    handleDeleteMember,
    handleEditMember,
    handleAddMembersViaFile,
    handleAddMembersFromList,
    isLoading,
    isSaveDisabled,
    onSaveMembers,
    memberModalData,
    groupDetails,
    currentGroupDetails,
    groupPaymentCollectionType,
    groupStartDate,
    groupEndDate,
    getGroupDueDate,
    isImmediatePayment,
    isCustomTerm,
    tabWaitingToSwitch,
    onTabSwitchConfirm,
    onTabSwitchCancel,
    groupInfoAddMemberTableColumn,
    validateMemberWithApi,
    organizationId,
    branchId,
    isAttendanceBasedFee,
    isSessionBased,
    sessionsPerCycle,
    showPastDueDateModal,
    isAllDueDatePast,
    handleConfirmPastDueDate,
    handleCancelPastDueDate
  } = useAddMembersStep(groupId);

  useEffect(() => {
    onHeaderStateChange({
      onSave: onSaveMembers,
      isSaveDisabled,
      isLoading
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSaveDisabled, isLoading]);

  if (isLoading && !isAddMemberModalOpen && !isEditMemberModalOpen) {
    return (
      <div className="flex h-96 items-center justify-center">
        <Spinner />
      </div>
    );
  }

  return (
    <>
      <div className="rounded-xl border border-cofeeGreyBlue200 bg-white py-6">
        <div className="flex items-center justify-between border-b border-cofeeGreyBlue200 px-6 pb-6">
          <h2 className="text-xl font-semibold text-cofeeNeutral10">
            Add Members
          </h2>
        </div>

        <div className="mt-2 flex justify-between px-6">
          <div className="w-full">
            <Tabs
              tabs={groupAddMemberTabs}
              selectedTab={selectedTab}
              onTabSelect={onSelectTab}
              type="secondary"
            />
          </div>
          {selectedTab?.id === AddMembersTabType.ADD_MANUALLY &&
            memberList.length >= 1 && (
              <Button
                variant="secondary"
                size="medium"
                className="!mt-4 min-w-40 rounded-full"
                handleClick={openAddMemberModal}
                leftIconName="plus"
                leftIconStroke={theme.primary}
                label="Add Member"
              />
            )}
        </div>

        <div className="rounded-b-lg">
          {!groupAddMemberTabs.length && (
            <p className="px-6 py-8 text-center text-sm text-cofeeNeutral8">
              You don&apos;t have permission to add members to this group.
            </p>
          )}
          {selectedTab?.id === AddMembersTabType.IMPORT_FILE && (
            <ImportFileTab
              handleAdd={handleAddMembersViaFile}
              groupAmount={
                groupDetails.groupAmount || currentGroupDetails?.amount
              }
              groupDueDate={getGroupDueDate()}
              groupFeeConfigurations={
                // eslint-disable-next-line no-nested-ternary
                isCustomTerm
                  ? currentGroupDetails?.payment_schedule?.term_details[0]
                      .fee_split
                  : groupDetails?.feeCategories?.length > 0
                  ? groupDetails?.feeCategories
                  : currentGroupDetails?.group_fee_amount_configuration || []
              }
              isOnce={
                (groupDetails.frequency || currentGroupDetails?.frequency) ===
                'once'
              }
              groupFrequency={
                groupDetails?.frequency || currentGroupDetails?.frequency
              }
              groupIntervalFrequency={
                groupDetails?.interval ||
                currentGroupDetails?.interval_frequency
              }
              isCustomTerm={isCustomTerm}
              termsData={currentGroupDetails?.payment_schedule?.term_details}
              groupStartDate={currentGroupDetails?.payment_schedule?.start_date}
              groupEndDate={currentGroupDetails?.payment_schedule?.end_date}
              isImmediatePayment={isImmediatePayment}
              isFeeCategoryEnabled={
                groupDetails?.isFeeCategoryEnabled ||
                currentGroupDetails?.is_fee_configured
              }
              isAttendanceBasedFee={isAttendanceBasedFee}
              isSessionBased={isSessionBased}
              sessionsPerCycle={sessionsPerCycle}
              activeGroupId={groupId}
              defaultTermLabel={defaultTermLabel}
              mixpanelGroupContext={
                currentGroupDetails
                  ? getGroupAnalyticsSummary(currentGroupDetails)
                  : undefined
              }
            />
          )}
          {selectedTab?.id === AddMembersTabType.ADD_FROM_MEMBER_LIST && (
            <AddFromMembersListTab
              branchId={branchId || ''}
              organizationId={organizationId || ''}
              groupAmount={
                groupDetails.groupAmount || currentGroupDetails?.amount
              }
              groupDueDate={getGroupDueDate()}
              groupStartDate={groupStartDate}
              groupName={groupDetails.name || currentGroupDetails?.name}
              handleAddMembers={handleAddMembersFromList}
              isFeeCategoryEnabled={
                groupDetails?.isFeeCategoryEnabled ||
                currentGroupDetails?.is_fee_configured
              }
              feeCategoryData={
                groupDetails?.feeCategories?.length > 0
                  ? groupDetails?.feeCategories
                  : currentGroupDetails?.group_fee_amount_configuration
              }
              isActiveGroup={!!groupDetails.isActive}
              groupPaymentCollectionType={groupPaymentCollectionType}
              groupFrequency={
                groupDetails?.interval ||
                currentGroupDetails?.interval_frequency
              }
              isImmediatePayment={isImmediatePayment}
              isCustomTerm={isCustomTerm}
              selectedRows={selectedRows}
              setSelectedRows={setSelectedRows}
              isAttendanceBasedFee={isAttendanceBasedFee}
              isSessionBased={isSessionBased}
              sessionsPerCycle={sessionsPerCycle}
              defaultTermLabel={defaultTermLabel}
              mixpanelGroupContext={
                currentGroupDetails
                  ? getGroupAnalyticsSummary(currentGroupDetails)
                  : undefined
              }
            />
          )}
          {selectedTab?.id === AddMembersTabType.ADD_MANUALLY && (
            <div className="min-h-120 w-full rounded-xl bg-theme">
              <AddManuallyTab
                handleAdd={openAddMemberModal}
                memberList={memberList}
                handleDelete={handleDeleteMember}
                handleEdit={handleEditMember}
                maxTableHeight={`${
                  memberList.length > 0 ? 'h-[calc(100vh-300px)]' : ''
                }`}
                columnHeaderList={groupInfoAddMemberTableColumn}
                isImmediatePayment={isImmediatePayment}
                isCustomTerm={isCustomTerm}
                isAttendanceBasedFee={isAttendanceBasedFee}
                isSessionBased={isSessionBased}
                groupSessionsPerCycle={sessionsPerCycle}
                defaultTermLabel={defaultTermLabel}
              />
            </div>
          )}
        </div>
      </div>

      {isAddMemberModalOpen && (
        <AddMemberModal
          {...memberModalData.add}
          isFeeCategoryEnabled={
            groupDetails?.isFeeCategoryEnabled ||
            currentGroupDetails?.is_fee_configured
          }
          feeCategoryData={
            groupDetails?.feeCategories?.length > 0
              ? groupDetails?.feeCategories
              : currentGroupDetails?.group_fee_amount_configuration
          }
          paymentCollectionType={groupPaymentCollectionType}
          memberList={memberList}
          groupStartDate={groupStartDate}
          groupEndDate={groupEndDate}
          isImmediatePayment={isImmediatePayment}
          isCustomTerm={isCustomTerm}
          termsList={getTermsListFromConfig({
            termsList:
              groupDetails.termsList ||
              currentGroupDetails?.payment_schedule?.term_details
          })}
          validateMember={validateMemberWithApi}
          isValidationLoading={isLoading}
          isAttendanceBasedFee={isAttendanceBasedFee}
          isSessionBased={isSessionBased}
          groupSessionPerCycle={sessionsPerCycle}
        />
      )}
      {isEditMemberModalOpen && (
        <AddMemberModal
          {...memberModalData.edit}
          selectedmember={selectedMember}
          isFeeCategoryEnabled={
            groupDetails?.isFeeCategoryEnabled ||
            currentGroupDetails?.is_fee_configured
          }
          feeCategoryData={
            // eslint-disable-next-line no-nested-ternary
            selectedMember?.feeCategoryData &&
            selectedMember?.feeCategoryData?.length > 0
              ? selectedMember?.feeCategoryData
              : groupDetails?.feeCategories?.length > 0
              ? groupDetails?.feeCategories
              : currentGroupDetails?.group_fee_amount_configuration
          }
          paymentCollectionType={groupPaymentCollectionType}
          memberList={memberList}
          groupStartDate={groupStartDate}
          groupEndDate={groupEndDate}
          isImmediatePayment={isImmediatePayment}
          isCustomTerm={isCustomTerm}
          termsList={selectedMember?.termsList}
          validateMember={validateMemberWithApi}
          isValidationLoading={isLoading}
          isAttendanceBasedFee={isAttendanceBasedFee}
          isSessionBased={isSessionBased}
          groupSessionPerCycle={sessionsPerCycle}
        />
      )}
      {tabWaitingToSwitch && (
        <ConfirmationModal
          isOpen
          title="Switching?"
          message="Switching will result in the loss of added member data."
          confirmButtonLabel="Continue"
          ModalIcon={SwitchingIcon}
          handleCancel={onTabSwitchCancel}
          handleConfirm={onTabSwitchConfirm}
          className="max-w-312"
          cancelButtonStyle="text-content"
          confirmButtonStyle="text-primary"
        />
      )}
      {showPastDueDateModal && (
        <ConfirmationModalV2
          onConfirm={handleConfirmPastDueDate}
          onCancel={handleCancelPastDueDate}
        >
          <div className="flex flex-col gap-2 text-center">
            <span className="text-base font-medium text-cofeeNeutral8">
              Amount for Terms with due date already past will be set as{' '}
              <span className="mr-0.5 font-inter">₹</span>0.{' '}
              {isAllDueDatePast
                ? 'You can edit the amount manually later.'
                : 'You can edit the amount manually before the next term due date.'}
            </span>
            <span className="text-base font-medium text-cofeeNeutral8">
              Are you sure you want to continue?
            </span>
          </div>
        </ConfirmationModalV2>
      )}
    </>
  );
};

export default MemberStep;
